import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  X,
  MapPin,
  Calendar,
  Briefcase,
  DollarSign,
  User,
  Globe,
  CheckCircle,
  Activity,
  Edit2,
  Check,
  Pencil,
  EyeOff,
  Bell,
  Send,
  UserCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Job } from '../../../types/job';
import FullBlockingModal from '../../common/FullBlockingModal';
import { formatDateLocalized } from '../../../utils/dateUtils';
import i18n from '../../../i18n';
import { Button } from '../../common/Button';
import { InfoItem } from './AdminProfileDetailsModal';
import { AdminShadowJob, useAdminStore } from '../../../stores/adminStore';
import { getJobPublicationKey } from '../../../utils/jobUtils';

const padTwo = (value: number) => String(value).padStart(2, '0');
const SHADOW_RECRUITER_UNASSIGNED_LABEL = 'Not assigned';

const toLocalDatetimeInputValue = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = padTwo(date.getMonth() + 1);
  const dd = padTwo(date.getDate());
  const hh = padTwo(date.getHours());
  const min = padTwo(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

interface ExpiresAtEditorProps {
  jobId: string;
  expiresAt: string | null;
  onSaved?: (value: string) => void;
}

const ExpiresAtEditor: React.FC<ExpiresAtEditorProps> = ({ jobId, expiresAt, onSaved }) => {
  const { t } = useTranslation(['admin', 'common', 'jobs']);
  const { updateJobExpiresAt } = useAdminStore();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialValue = expiresAt
    ? toLocalDatetimeInputValue(new Date(expiresAt))
    : toLocalDatetimeInputValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
  const [draft, setDraft] = useState(initialValue);

  const displayValue = expiresAt
    ? formatDateLocalized(expiresAt, i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  const handleSave = async () => {
    const date = new Date(draft);
    if (Number.isNaN(date.getTime())) return;
    setSaving(true);
    try {
      const isoValue = date.toISOString();
      await updateJobExpiresAt(jobId, isoValue);
      setEditing(false);
      onSaved?.(isoValue);
    } catch (error) {
      console.error('Error updating expires_at:', error);
      toast.error(
        t('expires_at_update_error', {
          ns: 'admin',
          defaultValue: 'Failed to update quote submission deadline',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="text-gray-400 mt-0.5">
        <Calendar size={18} />
      </div>
      <div className="flex-1">
        <label htmlFor={`expires-at-input-${jobId}`} className="text-xs text-gray-500">
          {t('table_fields.expires_at', { ns: 'admin' })}
        </label>
        {editing ? (
          <div className="mt-1 flex items-center gap-2">
            <input
              id={`expires-at-input-${jobId}`}
              type="datetime-local"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-primary focus:outline-none"
              disabled={saving}
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded bg-primary p-1 text-white hover:opacity-90 disabled:opacity-50"
              aria-label={t('save', { ns: 'common', defaultValue: 'Save' })}
            >
              <Check size={14} />
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(initialValue);
                setEditing(false);
              }}
              disabled={saving}
              className="rounded border border-gray-300 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              aria-label={t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{displayValue}</p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-gray-400 hover:text-primary"
              aria-label={t('edit', { ns: 'common', defaultValue: 'Edit' })}
            >
              <Edit2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface SelectionDateEditorProps {
  jobId: string;
  selectionDate: string | null;
  onSaved?: (value: string | null) => void;
}

const SelectionDateEditor: React.FC<SelectionDateEditorProps> = ({
  jobId,
  selectionDate,
  onSaved,
}) => {
  const { t } = useTranslation(['admin', 'common', 'jobs']);
  const { updateJobSelectionDate } = useAdminStore();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialValue = selectionDate ? toLocalDatetimeInputValue(new Date(selectionDate)) : '';
  const [draft, setDraft] = useState(initialValue);

  const displayValue = selectionDate
    ? formatDateLocalized(selectionDate, i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  const handleSave = async () => {
    if (!draft) {
      setSaving(true);
      try {
        await updateJobSelectionDate(jobId, null);
        setEditing(false);
        onSaved?.(null);
      } catch (error) {
        console.error('Error clearing selection_date:', error);
        toast.error(
          t('selection_date_update_error', {
            ns: 'admin',
            defaultValue: 'Failed to update expected response date',
          })
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const date = new Date(draft);
    if (Number.isNaN(date.getTime())) return;
    setSaving(true);
    try {
      const isoValue = date.toISOString();
      await updateJobSelectionDate(jobId, isoValue);
      setEditing(false);
      onSaved?.(isoValue);
    } catch (error) {
      console.error('Error updating selection_date:', error);
      toast.error(
        t('selection_date_update_error', {
          ns: 'admin',
          defaultValue: 'Failed to update expected response date',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="text-gray-400 mt-0.5">
        <Calendar size={18} />
      </div>
      <div className="flex-1">
        <label htmlFor={`selection-date-input-${jobId}`} className="text-xs text-gray-500">
          {t('table_fields.selection_date', { ns: 'admin' })}
        </label>
        {editing ? (
          <div className="mt-1 flex items-center gap-2">
            <input
              id={`selection-date-input-${jobId}`}
              type="datetime-local"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-primary focus:outline-none"
              disabled={saving}
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded bg-primary p-1 text-white hover:opacity-90 disabled:opacity-50"
              aria-label={t('save', { ns: 'common', defaultValue: 'Save' })}
            >
              <Check size={14} />
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(initialValue);
                setEditing(false);
              }}
              disabled={saving}
              className="rounded border border-gray-300 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              aria-label={t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{displayValue}</p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-gray-400 hover:text-primary"
              aria-label={t('edit', { ns: 'common', defaultValue: 'Edit' })}
            >
              <Edit2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface AdminJobDetailsModalProps {
  onClose: () => void;
  job: Job | null;
  onClick?: () => void;
  onManageShadowRecruiter?: (job: AdminShadowJob) => void;
  onEdit?: () => void;
  onUnpublish?: () => void;
  onPublish?: () => void;
  onAssignToRecruiter?: () => void;
  showActivityHubLink?: boolean;
  onJobFieldUpdated?: (patch: Partial<Pick<Job, 'expires_at' | 'selection_date'>>) => void;
}

const AdminJobDetailsModal: React.FC<AdminJobDetailsModalProps> = ({
  onClose,
  job,
  onClick,
  onManageShadowRecruiter,
  onEdit,
  onUnpublish,
  onPublish,
  onAssignToRecruiter,
  showActivityHubLink = true,
  onJobFieldUpdated,
}) => {
  const { t } = useTranslation(['admin', 'common', 'jobs']);
  const navigate = useNavigate();
  const { forceNotifyMatchingFreelancers } = useAdminStore();
  const [notifying, setNotifying] = useState(false);

  if (!job) return null;

  const shadowJob = job as AdminShadowJob;
  const shadowRecruiterCompanyName = shadowJob.shadow_recruiter_company_name?.trim() || null;
  const hasShadowRecruiter =
    Boolean(shadowJob.shadow_recruiter_id) ||
    Boolean(shadowJob.is_shadow_recruiter) ||
    Boolean(shadowRecruiterCompanyName);
  const canModerate = job.status === 'open' || job.status === 'paused';
  const hasQuotes = (job.quotes_count ?? 0) > 0;
  const publicationKey = getJobPublicationKey(job);
  const canUnpublish = canModerate && publicationKey !== 'draft';
  const isUnclaimedShadowJob = Boolean(shadowJob.is_shadow_recruiter);
  const canPublish = job.status === 'open' && publicationKey === 'draft' && isUnclaimedShadowJob;
  // Broadened beyond literal unclaimed shadow jobs: admin_assign_jobs_to_recruiter also
  // accepts any job owned by an administrator's own profile (client.role === 'administrator'),
  // since admin_upsert_shadow_recruiter already rejects creating a shadow_recruiters placeholder
  // for an email that matches an existing account — without this, the primary use case (an admin
  // created a job for someone who already has an account) would be unreachable.
  const canAssignToRecruiter = isUnclaimedShadowJob || shadowJob.client?.role === 'administrator';
  const isExpired = Boolean(job.expires_at) && new Date(job.expires_at!) <= new Date();
  const canForceNotify = job.status === 'open' && publicationKey === 'published' && !isExpired;

  const handleForceNotify = async () => {
    setNotifying(true);
    try {
      const count = await forceNotifyMatchingFreelancers(job.id);
      if (count > 0) {
        toast.success(t('jobModeration.forceNotifySuccess', { ns: 'admin', count }));
      } else {
        toast.info(t('jobModeration.forceNotifyNoMatches', { ns: 'admin' }));
      }
    } catch (error) {
      console.error('Error force-notifying matching freelancers:', error);
      // JOB_NOT_ELIGIBLE_FOR_NOTIFY and unexpected errors share the same message today,
      // but are handled as distinct branches so a more specific copy can be added later.
      const message = error instanceof Error ? error.message : String(error);
      if (message === 'JOB_NOT_ELIGIBLE_FOR_NOTIFY') {
        toast.error(t('jobModeration.forceNotifyError', { ns: 'admin' }));
      } else {
        toast.error(t('jobModeration.forceNotifyError', { ns: 'admin' }));
      }
    } finally {
      setNotifying(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <FullBlockingModal>
      <div className="border-2xl overflow-hidden flex flex-col">
        <div className="flex justify-between items-start p-6 border-b border-gray-100">
          <div className="flex gap-4">
            <div className="h-16 w-16 bg-primary/10 rounded-lg flex items-center justify-center text-primary font-bold text-2xl">
              {job.title.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{job.title}</h2>
              <div className="flex gap-2 mt-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(job.status)}`}
                >
                  {t(`job_statuses.${job.status}`, { ns: 'common', defaultValue: job.status })}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                  {job.visibility === 'public' ? (
                    <Globe size={12} className="mr-1" />
                  ) : (
                    <User size={12} className="mr-1" />
                  )}
                  {t(`job_visibility_labels.${job.visibility}`, { ns: 'jobs' })}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 capitalize">
                  {t(`jobModeration.publication.${publicationKey}`, { ns: 'admin' })}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <InfoItem
              icon={<MapPin size={18} />}
              label={t('table_fields.city', { ns: 'admin', defaultValue: 'Ville' })}
              value={`${job.city?.name} ${job.on_site ? t('table_fields.on_site', { ns: 'admin', defaultValue: '(Sur site)' }) : t('table_fields.is_remote', { ns: 'admin', defaultValue: '(Remote)' })}`}
            />
            <InfoItem
              icon={<DollarSign size={18} />}
              label={t('table_fields.budget_range', { ns: 'admin', defaultValue: 'Budget' })}
              value={
                job.budget_range
                  ? `${job.budget_range.min_amount} - ${job.budget_range.max_amount}`
                  : '-'
              }
            />
            <InfoItem
              icon={<Calendar size={18} />}
              label={t('table_fields.start_date', { ns: 'admin', defaultValue: 'Date de début' })}
              value={job.start_date ? formatDateLocalized(job.start_date, i18n.language) : '-'}
            />
            <InfoItem
              icon={<User size={18} />}
              label={t('table_fields.client', { ns: 'admin', defaultValue: 'Client' })}
              value={
                job.client
                  ? `${job.client.first_name} ${job.client.last_name} (${job.client.company_name || 'N/A'})`
                  : '-'
              }
            />
            <InfoItem
              icon={<Briefcase size={18} />}
              label={t('shadowRecruiters.fieldLabel', {
                ns: 'admin',
                defaultValue: 'Shadow recruiter',
              })}
              value={
                hasShadowRecruiter
                  ? shadowRecruiterCompanyName || '-'
                  : t('shadowRecruiters.unassigned', {
                      ns: 'admin',
                      defaultValue: SHADOW_RECRUITER_UNASSIGNED_LABEL,
                    })
              }
            />
            <InfoItem
              icon={<Briefcase size={18} />}
              label={t('jobModeration.quotesCount', { ns: 'admin' })}
              value={String(job.quotes_count ?? 0)}
            />
            <ExpiresAtEditor
              jobId={job.id}
              expiresAt={job.expires_at}
              onSaved={value => onJobFieldUpdated?.({ expires_at: value })}
            />
            <SelectionDateEditor
              jobId={job.id}
              selectionDate={job.selection_date ?? null}
              onSaved={value => onJobFieldUpdated?.({ selection_date: value })}
            />
          </div>

          <hr className="border-gray-100" />

          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
              {t('navigation.show_details', { ns: 'common', defaultValue: "Détails de l'offre" })}
            </h3>

            {job.listing_type && (
              <InfoItem
                icon={<Briefcase size={18} />}
                label={t('table_fields.listing_type', { ns: 'admin' })}
                value={job.listing_type.code || '-'}
              />
            )}

            {job.description && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-1">
                  {t('table_fields.description', { ns: 'admin', defaultValue: 'Description' })}
                </p>
                <div className="text-sm text-gray-900 whitespace-pre-line bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {job.description}
                </div>
              </div>
            )}

            {job.languages && job.languages.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">
                  {t('table_fields.languages', { ns: 'admin', defaultValue: 'Langues' })}
                </p>
                <div className="flex flex-wrap gap-2">
                  {job.languages.map((lang, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <hr className="border-gray-300" />

        <div className="m-4 flex flex-row flex-wrap gap-3 justify-center">
          {onManageShadowRecruiter ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={() => onManageShadowRecruiter(shadowJob)}
            >
              <Briefcase size={16} className="mr-2" />
              {t('shadowRecruiters.manageForJob', {
                ns: 'admin',
                defaultValue: hasShadowRecruiter
                  ? 'Manage shadow recruiter'
                  : 'Assign shadow recruiter',
              })}
            </Button>
          ) : null}
          {showActivityHubLink ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={() => {
                onClose();
                navigate(`/admin/jobs/${job.id}/hub`);
              }}
            >
              <Activity size={16} className="mr-2" />
              {t('jobActivityHub.open_hub', { ns: 'admin', defaultValue: 'Activity hub' })}
            </Button>
          ) : null}
          {canModerate && onEdit ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={onEdit}
            >
              <Pencil size={16} className="mr-2" />
              {t('jobModeration.editButton', { ns: 'admin' })}
            </Button>
          ) : null}
          {canPublish && onPublish ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={onPublish}
            >
              <Send size={16} className="mr-2" />
              {t('jobModeration.publishButton', { ns: 'admin' })}
            </Button>
          ) : null}
          {canAssignToRecruiter && onAssignToRecruiter ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={onAssignToRecruiter}
            >
              <UserCheck size={16} className="mr-2" />
              {t('jobModeration.assignToRecruiterButton', { ns: 'admin' })}
            </Button>
          ) : null}
          {canUnpublish && onUnpublish ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={onUnpublish}
            >
              <EyeOff size={16} className="mr-2" />
              {hasQuotes
                ? t('jobModeration.pauseButton', { ns: 'admin' })
                : t('jobModeration.unpublishButton', { ns: 'admin' })}
            </Button>
          ) : null}
          {canForceNotify ? (
            <Button
              variant="alternate"
              type="button"
              className="inline-flex items-center"
              onClick={handleForceNotify}
              disabled={notifying}
            >
              <Bell size={16} className="mr-2" />
              {t('jobModeration.forceNotifyButton', { ns: 'admin' })}
            </Button>
          ) : null}
          <Button variant="primary" onClick={onClick}>
            <CheckCircle size={16} className="mr-2" />
            {t('update_status_button', { ns: 'common', defaultValue: 'Modifier le statut' })}
          </Button>
        </div>
      </div>
    </FullBlockingModal>
  );
};

export default AdminJobDetailsModal;
